/**
 * Shared utilities for parsing delimited JSON blocks from LLM messages.
 *
 * Both [[ASK]] and [[PLAN]] blocks follow the same pattern:
 * 1. Match a regex with [[TAG]]...[[/TAG]] delimiters
 * 2. Extract text before/after the block
 * 3. Parse the JSON content (with repair fallback for common LLM issues)
 */

/**
 * Result of extracting a delimited JSON block from a message.
 */
export interface ParsedBlock<T> {
  before: string;
  parsed: T | null;
  after: string;
}

/**
 * Attempt to repair common LLM JSON issues.
 * LLMs often output literal newlines inside JSON string values or trailing commas,
 * both of which cause JSON.parse to fail.
 */
function repairLLMJSON(json: string): string {
  // Replace literal newlines/carriage returns with spaces.
  // This is safe because JSON whitespace between tokens is interchangeable,
  // and string values with newlines become spaces (acceptable for display).
  // Note: escaped sequences like \n (backslash + n) are NOT affected.
  let repaired = json.replace(/\r?\n/g, " ");

  // Remove trailing commas before ] or } (common LLM mistake)
  repaired = repaired.replace(/,\s*([}\]])/g, "$1");

  return repaired;
}

/**
 * Collect positions of a specific character outside JSON string literals.
 */
function findBracePositions(json: string, brace: string): number[] {
  const positions: number[] = [];
  let inString = false;
  let escaped = false;

  for (let i = 0; i < json.length; i++) {
    const ch = json[i];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (ch === "\\") {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (!inString && ch === brace) positions.push(i);
  }

  return positions;
}

/**
 * Fix mismatched braces in LLM JSON by trying to remove one extra brace
 * until JSON.parse succeeds. Handles both extra `}` and extra `{`.
 *
 * Common LLM errors:
 *   Extra }: {"changes":[...],"dataFlow":{...}}},"note":"..."}
 *   Extra {: {{"changes":[...],"dataFlow":{...}},"note":"..."}
 *
 * Strategy: count { vs } outside strings. If unbalanced, try removing
 * each instance of the excess brace type one at a time until parse succeeds.
 * Each attempt creates a temporary string — the original is never modified.
 * Returns the parsed object, or null if no single removal fixes the JSON.
 */
function rebalanceBraces<T>(json: string): T | null {
  const openPositions = findBracePositions(json, "{");
  const closePositions = findBracePositions(json, "}");

  // Determine which brace type is in excess.
  let targets: number[];
  if (closePositions.length > openPositions.length) {
    // Extra } — try removing each from right to left (skip the last one).
    targets = closePositions.slice(0, -1).reverse();
  } else if (openPositions.length > closePositions.length) {
    // Extra { — try removing each from left to right (skip the first one).
    targets = openPositions.slice(1);
  } else {
    // Balanced count but still invalid — try removing each } right-to-left
    // as a general fallback (handles structural misplacement).
    targets = closePositions.slice(0, -1).reverse();
  }

  for (const pos of targets) {
    const candidate = json.slice(0, pos) + json.slice(pos + 1);
    try {
      return JSON.parse(candidate) as T;
    } catch {
      // Try next position
    }
  }

  return null;
}

/**
 * Extract and parse a delimited JSON block from a message.
 *
 * The regex must have exactly one capture group containing the JSON content.
 * Returns the text before/after the block and the parsed JSON object.
 * Falls back to JSON repair if the initial parse fails.
 */
export function parseLLMJsonBlock<T>(message: string, regex: RegExp, blockName: string): ParsedBlock<T> {
  const match = message.match(regex);

  if (!match) {
    return { before: message, parsed: null, after: "" };
  }

  const [fullMatch, jsonContent] = match;
  const matchIndex = match.index!;

  const before = message.slice(0, matchIndex).trim();
  const after = message.slice(matchIndex + fullMatch.length).trim();

  let parsed: T | null = null;
  try {
    parsed = JSON.parse(jsonContent);
  } catch {
    try {
      parsed = JSON.parse(repairLLMJSON(jsonContent));
    } catch {
      // Final fallback: try removing extra closing braces one at a time.
      // LLMs sometimes output an extra } that closes the object early,
      // orphaning fields like "note" outside the main object:
      //   {"changes":[...],"dataFlow":{...}}},"note":"..."}
      // rebalanceBraces tries removing each } until JSON.parse succeeds,
      // preserving all fields including the orphaned ones.
      parsed = rebalanceBraces<T>(repairLLMJSON(jsonContent));
      if (!parsed) {
        console.error(`Failed to parse [[${blockName}]] block JSON even after brace rebalancing`);
        return { before: message, parsed: null, after: "" };
      }
    }
  }

  return { before, parsed, after };
}
